import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getProducts } from "../../utils/storage";

export default function BestSellers() {
    const navigate = useNavigate();
    const cardRefs = useRef([]);
    const [products, setProducts] = useState([]);

    useEffect(() => {
        document.title = "Best Sellers | Tecniqa";
    }, []);

    useEffect(() => {
        const observers = cardRefs.current.map((el, i) => {
            if (!el) return null;
            const obs = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setTimeout(() => el.classList.add("na-visible"), i * 90);
                        obs.disconnect();
                    }
                },
                { threshold: 0.1 }
            );
            obs.observe(el);
            return obs;
        });
        return () => observers.forEach((o) => o?.disconnect());
    }, [products]);

    useEffect(() => {
        const allProducts = getProducts();
        const filtered = allProducts.filter(
            (product) =>
                product.isActive !== false &&
                product.badge?.toLowerCase() === "best seller"
        );
        setProducts(filtered);
    }, []);

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
                @import url('https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css');

                :root {
                    --dark: #1B211A;
                    --green: #628141;
                    --greenSoft: #8BAE66;
                    --white: #ffffff;
                }

                .na-page {
                    margin-top: 0%;
                    font-family: 'DM Sans', sans-serif;
                    min-height: 100vh;
                    background: linear-gradient(180deg, #eef6ee 0%, #e4f0e3 60%, #f7faf7 100%);
                    padding: 0 0 80px;
                    position: relative;
                    overflow: hidden;
                }

                .na-blob {
                    position: absolute;
                    border-radius: 50%;
                    filter: blur(100px);
                    pointer-events: none;
                }
                .na-blob-1 {
                    width: 480px; height: 480px;
                    top: -160px; right: -100px;
                    background: rgba(98,129,65,0.10);
                }
                .na-blob-2 {
                    width: 320px; height: 320px;
                    bottom: -80px; left: -60px;
                    background: rgba(139,174,102,0.10);
                }

                .na-page-grid {
                    position: absolute;
                    inset: 0;
                    background-image:
                        linear-gradient(rgba(98,129,65,0.03) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(98,129,65,0.03) 1px, transparent 1px);
                    background-size: 52px 52px;
                    pointer-events: none;
                }

                .na-hero {
                    position: relative;
                    z-index: 1;
                    padding: 32px 8% 52px;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-end;
                    flex-wrap: wrap;
                    gap: 28px;
                }

                .na-eyebrow {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 0.12em;
                    text-transform: uppercase;
                    color: var(--green);
                    margin-bottom: 12px;
                }

                .na-eyebrow-line {
                    width: 28px; height: 2px;
                    background: var(--green);
                    border-radius: 2px;
                }

                .na-hero h1 {
                    font-family: 'Barlow Condensed', sans-serif;
                    font-size: clamp(2rem, 4.5vw, 3rem);
                    font-weight: 800;
                    color: var(--dark);
                    line-height: 1.08;
                    letter-spacing: -0.02em;
                    margin: 0 0 14px;
                }

                .na-hero h1 span { color: var(--green); }

                .na-hero p {
                    font-size: 15px;
                    color: #5b5b5b;
                    line-height: 1.75;
                    max-width: 460px;
                    margin: 0;
                }

                .na-btn-back {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    padding: 10px 20px;
                    border: 1.5px solid #d9d9d9;
                    border-radius: 100px;
                    background: #fff;
                    font-family: 'DM Sans', sans-serif;
                    font-size: 13.5px;
                    font-weight: 600;
                    color: #111;
                    cursor: pointer;
                    transition: all 0.22s ease;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                    flex-shrink: 0;
                    min-height: 44px;
                }

                .na-btn-back:hover {
                    border-color: var(--green);
                    color: var(--green);
                    background: rgba(98,129,65,0.06);
                    transform: translateX(-3px);
                }

                .na-grid {
                    position: relative;
                    z-index: 1;
                    padding: 0 8%;
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(255px, 1fr));
                    gap: 24px;
                }

                .na-empty {
                    position: relative;
                    z-index: 1;
                    padding: 80px 8%;
                    text-align: center;
                    color: #aaa;
                    font-size: 14px;
                }

                .na-card {
                    border-radius: 20px;
                    overflow: hidden;
                    background: linear-gradient(180deg, #ffffff 0%, #f7f7f7 100%);
                    border: 1px solid rgba(0,0,0,0.06);
                    box-shadow: 0 4px 24px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04);
                    display: flex;
                    flex-direction: column;
                    cursor: pointer;
                    position: relative;
                    opacity: 0;
                    transform: translateY(28px);
                    transition:
                        transform 0.35s cubic-bezier(0.4,0,0.2,1),
                        box-shadow 0.35s cubic-bezier(0.4,0,0.2,1),
                        border-color 0.25s,
                        opacity 0.4s ease;
                }

                .na-card.na-visible {
                    opacity: 1;
                    transform: translateY(0);
                }

                .na-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 0 0 1.5px rgba(98,129,65,0.35);
                    border-color: rgba(98,129,65,0.35);
                }

                .na-badge {
                    position: absolute;
                    top: 14px; left: 14px;
                    z-index: 3;
                    font-size: 10px;
                    font-weight: 700;
                    letter-spacing: 0.08em;
                    text-transform: uppercase;
                    color: #fff;
                    padding: 4px 12px;
                    border-radius: 100px;
                    background: linear-gradient(135deg, var(--green), var(--greenSoft));
                    box-shadow: 0 3px 10px rgba(98,129,65,0.35);
                }

                .na-card-actions {
                    position: absolute;
                    top: 14px; right: 14px;
                    z-index: 3;
                    opacity: 0;
                    transform: translateX(10px);
                    transition: all 0.25s cubic-bezier(0.4,0,0.2,1);
                }

                .na-card:hover .na-card-actions {
                    opacity: 1;
                    transform: translateX(0);
                }

                .na-action-btn {
                    width: 34px; height: 34px;
                    border-radius: 10px;
                    border: 1.5px solid rgba(0,0,0,0.08);
                    background: rgba(255,255,255,0.92);
                    backdrop-filter: blur(8px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 15px;
                    color: #444;
                    cursor: pointer;
                    transition: all 0.18s;
                }

                .na-action-btn:hover {
                    border-color: var(--green);
                    color: var(--green);
                    background: rgba(98,129,65,0.08);
                    transform: scale(1.1);
                }

                .na-img-wrap {
                    height: 200px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 24px;
                    background: radial-gradient(circle at 50% 30%, rgba(98,129,65,0.12) 0%, rgba(245,245,245,0.7) 70%, transparent 100%);
                    position: relative;
                    overflow: hidden;
                }

                .na-img-wrap::after {
                    content: '';
                    position: absolute;
                    bottom: 0; left: 0; right: 0;
                    height: 1px;
                    background: linear-gradient(90deg, transparent, rgba(98,129,65,0.18), transparent);
                }

                .na-img-wrap img {
                    max-height: 145px;
                    max-width: 100%;
                    object-fit: contain;
                    filter: drop-shadow(0 8px 20px rgba(0,0,0,0.10));
                    transition: transform 0.45s cubic-bezier(0.4,0,0.2,1);
                }

                .na-card:hover .na-img-wrap img {
                    transform: scale(1.1) rotate(-1deg);
                    filter: drop-shadow(0 14px 28px rgba(98,129,65,0.18));
                }

                .na-card-body {
                    padding: 18px 18px 0;
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .na-brand {
                    font-size: 10.5px;
                    font-weight: 700;
                    letter-spacing: 0.1em;
                    text-transform: uppercase;
                    color: var(--green);
                }

                .na-title {
                    font-size: 14px;
                    font-weight: 600;
                    color: #111;
                    line-height: 1.45;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    flex: 1;
                }

                .na-stock {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    font-size: 11.5px;
                    font-weight: 600;
                    color: var(--green);
                    padding-bottom: 4px;
                }

                .na-stock-dot {
                    width: 6px; height: 6px;
                    border-radius: 50%;
                    background: var(--green);
                    box-shadow: 0 0 6px rgba(98,129,65,0.5);
                }

                .na-card-footer {
                    padding: 12px 18px 18px;
                }

                .na-cta {
                    width: 100%;
                    padding: 11px;
                    border: none;
                    border-radius: 12px;
                    background: #111;
                    color: #fff;
                    font-family: 'DM Sans', sans-serif;
                    font-size: 13px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    min-height: 44px;
                    letter-spacing: 0.02em;
                    opacity: 0;
                    transform: translateY(8px);
                    transition:
                        opacity 0.28s cubic-bezier(0.4,0,0.2,1),
                        transform 0.28s cubic-bezier(0.4,0,0.2,1),
                        background 0.2s;
                }

                .na-img-placeholder {
                    width: 80px;
                    height: 80px;
                    border-radius: 50%;
                    background: rgba(98,129,65,0.10);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 32px;
                    color: rgba(98,129,65,0.4);
                }

                .na-card:hover .na-cta {
                    opacity: 1;
                    transform: translateY(0);
                }

                .na-cta:hover { background: var(--green); }

                @media (max-width: 900px) {
                    .na-hero { padding: 56px 6% 36px; }
                    .na-grid { padding: 0 6%; gap: 18px; }
                }

                @media (max-width: 640px) {
                    .na-hero { padding: 48px 5% 28px; flex-direction: column; align-items: flex-start; }
                    .na-grid { padding: 0 5%; grid-template-columns: repeat(auto-fill, minmax(220px,1fr)); gap: 14px; }
                    .na-cta { opacity: 1; transform: translateY(0); }
                    .na-card-actions { opacity: 1; transform: translateX(0); }
                }

                @media (max-width: 480px) {
                    .na-grid { grid-template-columns: 1fr 1fr; gap: 12px; }
                    .na-card { border-radius: 14px; }
                    .na-img-wrap { height: 140px; padding: 14px; }
                    .na-card-body { padding: 12px 12px 0; }
                    .na-card-footer { padding: 10px 12px 14px; }
                }
            `}</style>

            <div className="na-page">
                <div className="na-blob na-blob-1" />
                <div className="na-blob na-blob-2" />
                <div className="na-page-grid" />

                <div className="na-hero">
                    <div>
                        <div className="na-eyebrow">
                            <div className="na-eyebrow-line" />
                            Top Performers
                        </div>
                        <h1>Best <span>Sellers</span></h1>
                        <p>
                            The most trusted HVACR products chosen by professionals across the industry.
                        </p>
                    </div>
                    <button className="na-btn-back" onClick={() => window.history.back()}>
                        <i className="ri-arrow-left-line" />
                        Back
                    </button>
                </div>

                {products.length === 0 && (
                    <div className="na-empty">
                        No best sellers yet. Tag a product as "Best Seller" from the admin panel.
                    </div>
                )}

                <div className="na-grid">
                    {products.map((product, i) => (
                        <div
                            key={product.id}
                            className="na-card"
                            ref={(el) => (cardRefs.current[i] = el)}
                            onClick={() =>
                                navigate(`/category/${product.categorySlug}/${product.id}`)
                            }
                        >
                            <div className="na-badge">Best Seller</div>

                            <div className="na-card-actions">
                                <button
                                    className="na-action-btn"
                                    title="View product"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/category/${product.categorySlug}/${product.id}`);
                                    }}
                                >
                                    <i className="ri-eye-line" />
                                </button>
                            </div>

                            <div className="na-img-wrap">
                                {product.images?.[0] ? (
                                    <img src={product.images[0]} alt={product.name} />
                                ) : (
                                    <div className="na-img-placeholder">
                                        <i className="ri-image-line" />
                                    </div>
                                )}
                            </div>

                            <div className="na-card-body">
                                <div className="na-brand">{product.brand}</div>
                                <div className="na-title">{product.name}</div>
                                <div className="na-stock">
                                    <div className="na-stock-dot" />
                                    {product.availability} · Fast Shipping
                                </div>
                            </div>

                            <div className="na-card-footer">
                                <button
                                    className="na-cta"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/category/${product.categorySlug}/${product.id}`);
                                    }}
                                >
                                    <i className="ri-eye-line" />
                                    View Product
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}